import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import {
  cobrarFacturaCheque, editarFactura, pagarFactura, liquidarSaldoFactura,
  revertirFactura, getPagareFactura, getReciboFactura,
} from "../lib/api";
import { fmt, fmtDate } from "../lib/utils";
import {
  Button, Input, Select, FormField, Dialog,
  Badge, Spinner, Empty, PageHeader, SearchInput,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
  Card, CardContent,
} from "../components/ui";
import { Plus, Trash2, CheckCircle2, AlertCircle, Clock, CreditCard, TrendingUp, Landmark } from "lucide-react";
import { toast } from "sonner";
import Pagare from "../components/Pagare";
import Recibo from "../components/Recibo";

const ESTADO_FAC = {
  PENDIENTE:       "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300",
  PARCIAL:         "bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300",
  COBRADO:         "bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300",
  ANULADA:         "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300",
  CHEQUE_RECIBIDO: "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300",
  CHEQUE_COBRADO:  "bg-teal-100 text-teal-700 border-teal-200 dark:bg-teal-900/30 dark:text-teal-300",
};

const ESTADO_FAC_LABEL = {
  PENDIENTE:       "PENDIENTE",
  PARCIAL:         "PARCIAL",
  COBRADO:         "COBRADO",
  ANULADA:         "ANULADA",
  CHEQUE_RECIBIDO: "CHEQUE RECIBIDO",
  CHEQUE_COBRADO:  "CHEQUE COBRADO",
};

function cobroUrgencia(fechaStr, estado) {
  if ((estado !== "PENDIENTE" && estado !== "PARCIAL") || !fechaStr) return null;
  const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
  const fecha = new Date(fechaStr); fecha.setHours(0, 0, 0, 0);
  const dias = Math.round((fecha - hoy) / 86400000);
  if (dias < 0)  return { cls: "text-red-600 font-semibold",   icon: "overdue", label: `Vencido hace ${Math.abs(dias)}d` };
  if (dias <= 5) return { cls: "text-amber-600 font-semibold", icon: "warning", label: `En ${dias}d` };
  return null;
}

// ─── Date helpers ─────────────────────────────────────────────────────────────
function weekRange(offsetWeeks = 0) {
  const now = new Date();
  const dow = now.getDay() || 7;
  const mon = new Date(now);
  mon.setDate(now.getDate() - dow + 1 + offsetWeeks * 7);
  mon.setHours(0, 0, 0, 0);
  const sun = new Date(mon);
  sun.setDate(mon.getDate() + 6);
  sun.setHours(23, 59, 59, 999);
  return [mon, sun];
}
function monthRange(offsetMonths = 0) {
  const now = new Date();
  const s = new Date(now.getFullYear(), now.getMonth() + offsetMonths, 1);
  const e = new Date(now.getFullYear(), now.getMonth() + offsetMonths + 1, 0, 23, 59, 59);
  return [s, e];
}
function inRange(dateStr, [start, end]) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  return d >= start && d <= end;
}

function emptyItem() { return { descripcion: "", cantidad: 1, precio_unitario: 0, tipo_iva: "IVA10" }; }

const STATUS_TABS = [
  { id: "TODO",            label: "Todos" },
  { id: "PENDIENTE",       label: "Pendientes" },
  { id: "PARCIAL",         label: "Parcial (pagaré)" },
  { id: "ATRASADO",        label: "Atrasados" },
  { id: "TARJETA",         label: "Tarjeta" },
  { id: "CHEQUE_RECIBIDO", label: "Cheque recibido" },
  { id: "COBRADO",         label: "Cobrados" },
];

const PERIODOS_PEND = [
  { id: "semana",  label: "Esta semana" },
  { id: "proxima", label: "Próxima semana" },
  { id: "mes",     label: "Este mes" },
];

const PERIODOS_COBR = [
  { id: "semana", label: "Esta semana" },
  { id: "pasada", label: "Semana pasada" },
  { id: "mes",    label: "Este mes" },
];

export default function Facturacion() {
  const [list,         setList]         = useState([]);
  const [clientes,     setClientes]     = useState([]);
  const [presups,      setPresups]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState("");
  const [open,         setOpen]         = useState(false);
  const [detail,       setDetail]       = useState(null);
  const [saving,       setSaving]       = useState(false);
  const [estadoF,      setEstadoF]      = useState("TODO");
  const [periodoP,     setPeriodoP]     = useState(null);
  const [periodoC,     setPeriodoC]     = useState(null);
  const [form,  setForm]  = useState({ cliente_id: "", presupuesto_id: "", timbrado: "", forma_pago: "EFECTIVO", observaciones: "" });
  const [items, setItems] = useState([emptyItem()]);
  const [aseg,  setAseg]  = useState({ aseguradora_id: "", monto_aseguradora: 0, monto_cliente: 0, franquicia: 0 });
  const [aseguradoras, setAseguradoras] = useState([]);

  const emptyEditForm = (fac) => ({ forma_pago: fac?.forma_pago ?? "EFECTIVO", timbrado: fac?.timbrado ?? "", fecha_estimada_pago: fac?.fecha_estimada_pago?.slice(0,10) ?? "", observaciones: fac?.observaciones ?? "", total_general: "" });
  const [editDialog,  setEditDialog]  = useState(null);
  const [editForm,    setEditForm]    = useState({});
  const [savingEdit,  setSavingEdit]  = useState(false);
  const se = (k, v) => setEditForm((f) => ({ ...f, [k]: v }));

  const emptyChequeSub = () => ({ banco: "", agencia: "", cuenta: "", numero_cheque: "", emitente: "", fecha_emision: "", fecha_vencimiento: "" });
  const emptyPagoForm = (fac) => ({ tipo: "COMPLETO", forma_pago: "EFECTIVO", monto: fac?.total_general ?? 0, fecha_vencimiento_saldo: "", cheque: emptyChequeSub() });
  const [pagoDialog,  setPagoDialog]  = useState(null);
  const [pagoForm,    setPagoForm]    = useState({});
  const [savingPago,  setSavingPago]  = useState(false);
  const sp  = (k, v) => setPagoForm((f) => ({ ...f, [k]: v }));
  const spc = (k, v) => setPagoForm((f) => ({ ...f, cheque: { ...f.cheque, [k]: v } }));

  const [liquidarDialog, setLiquidarDialog] = useState(null);
  const [liquidarForm,   setLiquidarForm]   = useState({ forma_pago: "EFECTIVO", cheque: emptyChequeSub() });
  const [savingLiquidar, setSavingLiquidar] = useState(false);
  const sl  = (k, v) => setLiquidarForm((f) => ({ ...f, [k]: v }));
  const slc = (k, v) => setLiquidarForm((f) => ({ ...f, cheque: { ...f.cheque, [k]: v } }));

  const [pagareView, setPagareView] = useState(null);
  const [reciboView, setReciboView] = useState(null);

  const load = useCallback(async () => {
    const [f, c, p, a] = await Promise.all([
      api.get("/facturas"),
      api.get("/clientes"),
      api.get("/presupuestos", { params: { estado: "APROBADO" } }),
      api.get("/aseguradoras"),
    ]);
    setList(f.data);
    setClientes(c.data);
    setPresups(p.data);
    setAseguradoras(a.data);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const s = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function onPresupSelect(pid) {
    s("presupuesto_id", pid);
    if (!pid) return;
    const pre = presups.find((p) => String(p.id) === String(pid));
    if (!pre) return;
    s("cliente_id", String(pre.cliente_id));
    setItems((pre.items ?? []).map((i) => ({ descripcion: i.descripcion, cantidad: i.cantidad, precio_unitario: i.precio_unitario, tipo_iva: "IVA10" })));
  }

  function updateItem(idx, field, val) {
    setItems((prev) => prev.map((it, j) =>
      j !== idx ? it : { ...it, [field]: ["cantidad", "precio_unitario"].includes(field) ? parseFloat(val) || 0 : val }
    ));
  }

  const exentas  = items.reduce((s, i) => s + (i.tipo_iva === "EXENTA" ? i.cantidad * i.precio_unitario : 0), 0);
  const b5       = items.reduce((s, i) => s + (i.tipo_iva === "IVA5"   ? i.cantidad * i.precio_unitario : 0), 0);
  const b10      = items.reduce((s, i) => s + (i.tipo_iva === "IVA10"  ? i.cantidad * i.precio_unitario : 0), 0);
  const iva5     = b5 * 5 / 105;
  const iva10    = b10 * 10 / 110;
  const totalIva = iva5 + iva10;
  const total    = exentas + b5 + b10;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.cliente_id) { toast.error("Seleccioná un cliente"); return; }
    setSaving(true);
    try {
      await api.post("/facturas", {
        cliente_id:     parseInt(form.cliente_id),
        presupuesto_id: form.presupuesto_id ? parseInt(form.presupuesto_id) : null,
        timbrado:       form.timbrado || null,
        forma_pago:     form.forma_pago,
        observaciones:  form.observaciones || null,
        aseguradora_id: aseg.aseguradora_id ? parseInt(aseg.aseguradora_id) : null,
        monto_aseguradora: aseg.monto_aseguradora,
        monto_cliente:     aseg.monto_cliente,
        franquicia:        aseg.franquicia,
        items: items.map((i) => ({ descripcion: i.descripcion, cantidad: i.cantidad, precio_unitario: i.precio_unitario, tipo_iva: i.tipo_iva })),
      });
      toast.success("Factura creada");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Error");
    } finally {
      setSaving(false);
    }
  }

  function pagar(fac) {
    setPagoForm(emptyPagoForm(fac));
    setPagoDialog(fac);
  }

  async function submitPago(e) {
    e.preventDefault();
    if (!pagoDialog) return;
    const total = pagoDialog.total_general;
    const esCheque = pagoForm.forma_pago === "CHEQUE";
    let monto = total;
    if (pagoForm.tipo === "PARCIAL") {
      monto = parseFloat(pagoForm.monto) || 0;
      if (monto <= 0 || monto >= total) {
        toast.error("El abono parcial debe ser mayor a 0 y menor al total");
        return;
      }
      if (!pagoForm.fecha_vencimiento_saldo) {
        toast.error("Indicá la fecha límite para el pago del saldo");
        return;
      }
    }
    if (esCheque && !pagoForm.cheque.numero_cheque) {
      toast.error("Indicá el número de cheque");
      return;
    }
    setSavingPago(true);
    try {
      if (esCheque) {
        await cobrarFacturaCheque(pagoDialog.id, {
          banco:             pagoForm.cheque.banco || null,
          agencia:           pagoForm.cheque.agencia || null,
          cuenta:            pagoForm.cheque.cuenta || null,
          numero_cheque:     pagoForm.cheque.numero_cheque || null,
          emitente:          pagoForm.cheque.emitente || null,
          valor:             monto,
          fecha_emision:     pagoForm.cheque.fecha_emision || null,
          fecha_vencimiento: pagoForm.cheque.fecha_vencimiento || null,
          fecha_vencimiento_saldo: pagoForm.tipo === "PARCIAL" ? pagoForm.fecha_vencimiento_saldo : null,
        });
      } else {
        await pagarFactura(pagoDialog.id, {
          forma_pago: pagoForm.forma_pago,
          monto,
          fecha_vencimiento_saldo: pagoForm.tipo === "PARCIAL" ? pagoForm.fecha_vencimiento_saldo : null,
        });
      }
      const facId = pagoDialog.id;
      setPagoDialog(null);
      load();
      if (pagoForm.tipo === "PARCIAL") {
        toast.success(esCheque ? "Cheque registrado como abono — se generó el pagaré del saldo" : "Cobro parcial registrado — se generó el pagaré del saldo");
        const p = await getPagareFactura(facId);
        setPagareView(p.data);
      } else if (esCheque) {
        toast.success("Cheque registrado — pendiente de cobro en el banco");
      } else {
        toast.success("Cobro registrado");
        const rec = await getReciboFactura(facId);
        setReciboView(rec.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Error al registrar cobro");
    } finally {
      setSavingPago(false);
    }
  }

  function liquidar(fac) {
    setLiquidarForm({ forma_pago: "EFECTIVO", cheque: emptyChequeSub() });
    setLiquidarDialog(fac);
  }

  async function submitLiquidar(e) {
    e.preventDefault();
    if (!liquidarDialog) return;
    const esCheque = liquidarForm.forma_pago === "CHEQUE";
    if (esCheque && !liquidarForm.cheque.numero_cheque) {
      toast.error("Indicá el número de cheque");
      return;
    }
    setSavingLiquidar(true);
    try {
      await liquidarSaldoFactura(liquidarDialog.id, {
        forma_pago:        liquidarForm.forma_pago,
        banco:             esCheque ? (liquidarForm.cheque.banco || null) : null,
        agencia:           esCheque ? (liquidarForm.cheque.agencia || null) : null,
        cuenta:            esCheque ? (liquidarForm.cheque.cuenta || null) : null,
        numero_cheque:     esCheque ? (liquidarForm.cheque.numero_cheque || null) : null,
        emitente:          esCheque ? (liquidarForm.cheque.emitente || null) : null,
        fecha_emision:     esCheque ? (liquidarForm.cheque.fecha_emision || null) : null,
        fecha_vencimiento: esCheque ? (liquidarForm.cheque.fecha_vencimiento || null) : null,
      });
      const facId = liquidarDialog.id;
      setLiquidarDialog(null);
      load();
      if (esCheque) {
        toast.success("Cheque registrado para el saldo — pendiente de cobro en el banco");
      } else {
        toast.success("Saldo liquidado — factura cobrada en su totalidad");
        const rec = await getReciboFactura(facId);
        setReciboView(rec.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Error al liquidar el saldo");
    } finally {
      setSavingLiquidar(false);
    }
  }

  async function revertir(fac) {
    if (!window.confirm(`¿Deshacer el cobro de ${fac.numero}? Volverá a estado PENDIENTE.`)) return;
    try {
      await revertirFactura(fac.id);
      toast.success("Cobro revertido");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Error al revertir el cobro");
    }
  }

  async function verPagare(fac) {
    try {
      const r = await getPagareFactura(fac.id);
      setPagareView(r.data);
    } catch {
      toast.error("No se pudo cargar el pagaré");
    }
  }

  async function verRecibo(fac) {
    try {
      const r = await getReciboFactura(fac.id);
      setReciboView(r.data);
    } catch {
      toast.error("No se pudo cargar el recibo");
    }
  }

  async function submitEdit(e) {
    e.preventDefault();
    if (!editDialog) return;
    setSavingEdit(true);
    try {
      await editarFactura(editDialog.id, {
        forma_pago:          editForm.forma_pago,
        timbrado:            editForm.timbrado || null,
        fecha_estimada_pago: editForm.fecha_estimada_pago || null,
        observaciones:       editForm.observaciones || null,
        total_general:       editDialog.origen === "AUTO" && editForm.total_general !== "" ? (v => isNaN(v) ? null : v)(parseFloat(editForm.total_general)) : null,
      });
      toast.success("Factura actualizada");
      setEditDialog(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Error al editar");
    } finally {
      setSavingEdit(false);
    }
  }

  async function anular(id) {
    if (!window.confirm("¿Anular esta factura?")) return;
    try {
      await api.patch(`/facturas/${id}/anular`);
      toast.success("Factura anulada");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Error al anular");
    }
  }

  // ─── Filter logic ──────────────────────────────────────────────────────────
  const hoy = new Date(); hoy.setHours(0, 0, 0, 0);

  function isAtrasado(f) {
    if (f.estado === "PENDIENTE" && f.fecha_estimada_pago) return new Date(f.fecha_estimada_pago) < hoy;
    if (f.estado === "PARCIAL"   && f.fecha_vencimiento_saldo) return new Date(f.fecha_vencimiento_saldo) < hoy;
    return false;
  }

  function getPeriodRange() {
    if (estadoF === "PENDIENTE" && periodoP) {
      if (periodoP === "semana")  return weekRange(0);
      if (periodoP === "proxima") return weekRange(1);
      if (periodoP === "mes")     return monthRange(0);
    }
    if (estadoF === "COBRADO" && periodoC) {
      if (periodoC === "semana") return weekRange(0);
      if (periodoC === "pasada") return weekRange(-1);
      if (periodoC === "mes")    return monthRange(0);
    }
    return null;
  }

  const periodRange = getPeriodRange();

  const filtered = list.filter((f) => {
    if (search) {
      const q = search.toLowerCase();
      if (!f.numero?.toLowerCase().includes(q) && !f.cliente_nombre?.toLowerCase().includes(q)) return false;
    }
    if (estadoF === "PENDIENTE"       && (f.estado !== "PENDIENTE" || isAtrasado(f))) return false;
    if (estadoF === "PARCIAL"         && (f.estado !== "PARCIAL" || isAtrasado(f)))   return false;
    if (estadoF === "COBRADO"         && f.estado !== "COBRADO" && f.estado !== "CHEQUE_COBRADO") return false;
    if (estadoF === "ATRASADO"        && !isAtrasado(f))          return false;
    if (estadoF === "TARJETA"         && f.forma_pago !== "TARJETA") return false;
    if (estadoF === "CHEQUE_RECIBIDO" && f.estado !== "CHEQUE_RECIBIDO") return false;
    if (periodRange) {
      const dateCheck = estadoF === "COBRADO" ? (f.fecha_pago ?? f.fecha) : f.fecha_estimada_pago;
      if (!inRange(dateCheck, periodRange)) return false;
    }
    return true;
  });

  // Global stats
  const statPendiente  = list.filter((f) => (f.estado === "PENDIENTE" || f.estado === "CHEQUE_RECIBIDO") && !isAtrasado(f)).reduce((s, f) => s + f.total_general, 0)
                        + list.filter((f) => f.estado === "PARCIAL" && !isAtrasado(f)).reduce((s, f) => s + f.saldo_pendiente, 0);
  const statAtrasado   = list.filter((f) => isAtrasado(f)).reduce((s, f) => s + (f.estado === "PARCIAL" ? f.saldo_pendiente : f.total_general), 0);
  const statCobradoMes = list.filter((f) => (f.estado === "COBRADO" || f.estado === "CHEQUE_COBRADO") && inRange(f.fecha_pago ?? f.fecha, monthRange(0))).reduce((s, f) => s + f.total_general, 0);
  const totalFiltrado  = filtered.reduce((s, f) => s + f.total_general, 0);
  const hasPeriodOrStatus = estadoF !== "TODO" || periodRange;

  if (loading) return <div className="flex items-center justify-center h-full"><Spinner className="w-8 h-8" /></div>;

  return (
    <div className="p-4 sm:p-6">
      <PageHeader title="Cobros del Taller" sub={`${list.length} registros`}>
        <Button onClick={() => {
          setForm({ cliente_id: "", presupuesto_id: "", timbrado: "", forma_pago: "EFECTIVO", observaciones: "" });
          setItems([emptyItem()]);
          setAseg({ aseguradora_id: "", monto_aseguradora: 0, monto_cliente: 0, franquicia: 0 });
          setOpen(true);
        }}>
          <Plus className="w-4 h-4" /> Nueva factura
        </Button>
      </PageHeader>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <button
          onClick={() => { setEstadoF("PENDIENTE"); setPeriodoP(null); setPeriodoC(null); }}
          className={`text-left p-4 rounded-lg border-2 transition-all ${
            estadoF === "PENDIENTE"
              ? "border-amber-400 bg-amber-50 dark:bg-amber-900/20"
              : "border-border bg-card hover:border-amber-300"
          }`}
        >
          <div className="flex items-center gap-2 mb-1">
            <Clock className="w-4 h-4 text-amber-500" />
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Por cobrar</span>
          </div>
          <p className="text-xl font-bold font-mono text-amber-600">{fmt(statPendiente)}</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {list.filter((f) => (f.estado === "PENDIENTE" || f.estado === "CHEQUE_RECIBIDO" || f.estado === "PARCIAL") && !isAtrasado(f)).length} pendientes
          </p>
        </button>

        <button
          onClick={() => { setEstadoF("ATRASADO"); setPeriodoP(null); setPeriodoC(null); }}
          className={`text-left p-4 rounded-lg border-2 transition-all ${
            estadoF === "ATRASADO"
              ? "border-red-400 bg-red-50 dark:bg-red-900/20"
              : "border-border bg-card hover:border-red-300"
          }`}
        >
          <div className="flex items-center gap-2 mb-1">
            <AlertCircle className="w-4 h-4 text-red-500" />
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Atrasados</span>
          </div>
          <p className="text-xl font-bold font-mono text-red-600">{fmt(statAtrasado)}</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {list.filter((f) => isAtrasado(f)).length} vencidos
          </p>
        </button>

        <button
          onClick={() => { setEstadoF("COBRADO"); setPeriodoP(null); setPeriodoC("mes"); }}
          className={`text-left p-4 rounded-lg border-2 transition-all ${
            estadoF === "COBRADO"
              ? "border-green-400 bg-green-50 dark:bg-green-900/20"
              : "border-border bg-card hover:border-green-300"
          }`}
        >
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle2 className="w-4 h-4 text-green-500" />
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Cobrado este mes</span>
          </div>
          <p className="text-xl font-bold font-mono text-green-600">{fmt(statCobradoMes)}</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {list.filter((f) => (f.estado === "COBRADO" || f.estado === "CHEQUE_COBRADO") && inRange(f.fecha_pago ?? f.fecha, monthRange(0))).length} cobros
          </p>
        </button>
      </div>

      {/* Status tabs */}
      <div className="flex gap-1 p-1 bg-muted rounded-lg w-fit mb-3 flex-wrap">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => {
              setEstadoF(tab.id);
              if (tab.id !== "PENDIENTE") setPeriodoP(null);
              if (tab.id !== "COBRADO")   setPeriodoC(null);
            }}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors font-medium whitespace-nowrap ${
              estadoF === tab.id
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {tab.label}
            {tab.id === "ATRASADO" && list.filter((f) => isAtrasado(f)).length > 0 && (
              <span className="ml-1.5 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                {list.filter((f) => isAtrasado(f)).length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Period sub-filter — Pendientes */}
      {estadoF === "PENDIENTE" && (
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <span className="text-xs text-muted-foreground">Cobro estimado:</span>
          {PERIODOS_PEND.map((p) => (
            <button
              key={p.id}
              onClick={() => setPeriodoP(periodoP === p.id ? null : p.id)}
              className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                periodoP === p.id
                  ? "bg-amber-100 border-amber-400 text-amber-700 dark:bg-amber-900/30 dark:border-amber-600 dark:text-amber-300 font-medium"
                  : "border-border text-muted-foreground hover:border-amber-300 hover:text-amber-600"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      )}

      {/* Period sub-filter — Cobrados */}
      {estadoF === "COBRADO" && (
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <span className="text-xs text-muted-foreground">Período cobrado:</span>
          {PERIODOS_COBR.map((p) => (
            <button
              key={p.id}
              onClick={() => setPeriodoC(periodoC === p.id ? null : p.id)}
              className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                periodoC === p.id
                  ? "bg-green-100 border-green-400 text-green-700 dark:bg-green-900/30 dark:border-green-600 dark:text-green-300 font-medium"
                  : "border-border text-muted-foreground hover:border-green-300 hover:text-green-600"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      )}

      {/* Period total bar */}
      {hasPeriodOrStatus && filtered.length > 0 && (
        <div className={`flex items-center justify-between px-4 py-2.5 rounded-lg mb-4 border ${
          estadoF === "COBRADO"
            ? "bg-green-50 border-green-200 dark:bg-green-900/10 dark:border-green-800"
            : estadoF === "ATRASADO"
              ? "bg-red-50 border-red-200 dark:bg-red-900/10 dark:border-red-800"
              : "bg-amber-50 border-amber-200 dark:bg-amber-900/10 dark:border-amber-800"
        }`}>
          <span className="text-sm text-muted-foreground">
            {filtered.length} registro{filtered.length !== 1 ? "s" : ""}
            {estadoF === "COBRADO" ? " cobrados" : estadoF === "ATRASADO" ? " vencidos" : " pendientes"}
          </span>
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-muted-foreground" />
            <span className="font-bold font-mono text-base">{fmt(totalFiltrado)}</span>
          </div>
        </div>
      )}

      <SearchInput value={search} onChange={setSearch} placeholder="Buscar…" className="w-full sm:w-64 mb-4" />

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>N° / Pres.</TableHead>
            <TableHead>Ingreso vehículo</TableHead>
            <TableHead>Cliente</TableHead>
            <TableHead>Timbrado</TableHead>
            <TableHead>Cobro estimado</TableHead>
            <TableHead className="text-right">Total</TableHead>
            <TableHead>Forma pago</TableHead>
            <TableHead>Estado</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtered.length === 0 && (
            <TableRow><TableCell colSpan={9}><Empty message="Sin registros para este filtro" /></TableCell></TableRow>
          )}
          {filtered.map((f) => (
            <TableRow key={f.id}>
              <TableCell className="font-mono text-xs">
                {f.numero}
                {f.origen === "AUTO" && <span className="ml-1 text-[10px] text-muted-foreground">(auto)</span>}
              </TableCell>
              <TableCell className="text-sm">{f.vehiculo_fecha_ingreso ? fmtDate(f.vehiculo_fecha_ingreso) : fmtDate(f.fecha)}</TableCell>
              <TableCell className="text-sm">{f.cliente_nombre}</TableCell>
              <TableCell className="font-mono text-xs">{f.timbrado ?? "—"}</TableCell>
              <TableCell className="text-xs">
                {(() => {
                  const fechaRef = f.estado === "PARCIAL" ? f.fecha_vencimiento_saldo : f.fecha_estimada_pago;
                  const urg = cobroUrgencia(fechaRef, f.estado);
                  if (!fechaRef) return <span className="text-muted-foreground">—</span>;
                  return (
                    <span className={urg ? urg.cls : ""}>
                      {urg?.icon === "overdue" && <AlertCircle className="inline w-3 h-3 mr-0.5 -mt-0.5" />}
                      {urg?.icon === "warning"  && <Clock      className="inline w-3 h-3 mr-0.5 -mt-0.5" />}
                      {fmtDate(fechaRef)}
                      {urg && <span className="ml-1 text-[10px]">({urg.label})</span>}
                    </span>
                  );
                })()}
              </TableCell>
              <TableCell className="text-right font-mono text-sm">
                {f.estado === "PARCIAL" ? (
                  <div>
                    <div className="font-semibold">{fmt(f.total_general)}</div>
                    <div className="text-[10px] text-orange-600">Saldo: {fmt(f.saldo_pendiente)}</div>
                  </div>
                ) : (
                  <span className="font-semibold">{fmt(f.total_general)}</span>
                )}
              </TableCell>
              <TableCell className="text-xs">
                {f.forma_pago === "TARJETA"  && <CreditCard className="inline w-3 h-3 mr-1 text-primary -mt-0.5" />}
                {f.forma_pago === "CHEQUE"   && <Landmark   className="inline w-3 h-3 mr-1 text-blue-500 -mt-0.5" />}
                {f.forma_pago}
              </TableCell>
              <TableCell>
                <Badge className={ESTADO_FAC[f.estado]}>{ESTADO_FAC_LABEL[f.estado] ?? f.estado}</Badge>
              </TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  <button onClick={() => setDetail(f)} className="text-xs text-primary hover:underline">Ver</button>
                  {f.estado !== "ANULADA" && (
                    <button
                      onClick={() => { setEditForm(emptyEditForm(f)); setEditDialog(f); }}
                      className="text-xs text-muted-foreground hover:underline ml-1"
                    >Editar</button>
                  )}
                  {f.estado === "PENDIENTE" && (
                    <button
                      onClick={() => pagar(f)}
                      className="text-xs text-green-600 hover:underline ml-1"
                    >Cobrar</button>
                  )}
                  {(f.estado === "PENDIENTE" || f.estado === "CHEQUE_RECIBIDO") && (
                    <button onClick={() => anular(f.id)} className="text-xs text-destructive hover:underline ml-1">Anular</button>
                  )}
                  {f.estado === "PARCIAL" && (
                    <>
                      <button onClick={() => liquidar(f)} className="text-xs text-green-600 hover:underline ml-1">Liquidar saldo</button>
                      <button onClick={() => verPagare(f)} className="text-xs text-muted-foreground hover:underline ml-1">Pagaré</button>
                      <button onClick={() => revertir(f)} className="text-xs text-destructive hover:underline ml-1">Deshacer</button>
                    </>
                  )}
                  {(f.estado === "COBRADO" || f.estado === "CHEQUE_COBRADO") && (
                    <>
                      <button onClick={() => verRecibo(f)} className="text-xs text-muted-foreground hover:underline ml-1">Recibo</button>
                      {f.estado === "COBRADO" && (
                        <button onClick={() => revertir(f)} className="text-xs text-destructive hover:underline ml-1">Deshacer</button>
                      )}
                    </>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Dialog nueva factura */}
      <Dialog open={open} onClose={() => setOpen(false)} title="Nueva Factura" size="2xl">
        <form onSubmit={handleSubmit}>
          <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
            <div className="flex-1 min-w-0 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <FormField label="Cliente *">
                  <Select value={form.cliente_id} onChange={(e) => s("cliente_id", e.target.value)} required>
                    <option value="">Seleccionar…</option>
                    {clientes.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                  </Select>
                </FormField>
                <FormField label="Desde presupuesto">
                  <Select value={form.presupuesto_id} onChange={(e) => onPresupSelect(e.target.value)}>
                    <option value="">Sin vinculación</option>
                    {presups.map((p) => <option key={p.id} value={p.id}>{p.numero} — {p.cliente_nombre}</option>)}
                  </Select>
                </FormField>
                <FormField label="Timbrado">
                  <Input value={form.timbrado} onChange={(e) => s("timbrado", e.target.value)} />
                </FormField>
                <FormField label="Forma de pago">
                  <Select value={form.forma_pago} onChange={(e) => s("forma_pago", e.target.value)}>
                    {["EFECTIVO", "TRANSFERENCIA", "TARJETA", "CHEQUE", "SEGURO"].map((p) => <option key={p}>{p}</option>)}
                  </Select>
                </FormField>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold">Ítems</span>
                  <Button type="button" variant="outline" size="sm" onClick={() => setItems((i) => [...i, emptyItem()])}>
                    <Plus className="w-3.5 h-3.5" /> Agregar
                  </Button>
                </div>
                <div className="overflow-x-auto">
                <div className="min-w-[460px]">
                {items.map((item, idx) => (
                  <div key={idx} className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-2 items-end mb-2">
                    <FormField label={idx === 0 ? "Descripción" : undefined}>
                      <Input value={item.descripcion} onChange={(e) => updateItem(idx, "descripcion", e.target.value)} required />
                    </FormField>
                    <FormField label={idx === 0 ? "Cant." : undefined}>
                      <Input type="number" min="0.01" step="0.01" className="w-20" value={item.cantidad} onChange={(e) => updateItem(idx, "cantidad", e.target.value)} />
                    </FormField>
                    <FormField label={idx === 0 ? "P. Unit" : undefined}>
                      <Input type="number" min="0" step="100" className="w-28" value={item.precio_unitario} onChange={(e) => updateItem(idx, "precio_unitario", e.target.value)} />
                    </FormField>
                    <FormField label={idx === 0 ? "IVA" : undefined}>
                      <Select value={item.tipo_iva} onChange={(e) => updateItem(idx, "tipo_iva", e.target.value)} className="w-24">
                        <option value="EXENTA">Exenta</option>
                        <option value="IVA5">5%</option>
                        <option value="IVA10">10%</option>
                      </Select>
                    </FormField>
                    <div className={idx === 0 ? "mt-5" : ""}>
                      <button type="button" onClick={() => setItems((i) => i.filter((_, j) => j !== idx))} disabled={items.length === 1} className="p-2 text-muted-foreground hover:text-destructive disabled:opacity-30">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
                </div>
                </div>
              </div>
            </div>

            <div className="w-full lg:w-56 shrink-0">
              <Card className="sticky top-0">
                <CardContent className="p-4 space-y-2 text-sm">
                  <p className="font-semibold font-display mb-3">Totales IVA</p>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Exentas</span>
                    <span className="font-mono">{fmt(exentas)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Gravadas 5%</span>
                    <span className="font-mono">{fmt(b5)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Gravadas 10%</span>
                    <span className="font-mono">{fmt(b10)}</span>
                  </div>
                  <div className="flex justify-between border-t border-border pt-2">
                    <span className="text-muted-foreground">IVA total</span>
                    <span className="font-mono">{fmt(totalIva)}</span>
                  </div>
                  <div className="flex justify-between font-bold text-base border-t border-border pt-2">
                    <span>TOTAL</span>
                    <span className="font-mono text-primary">{fmt(total)}</span>
                  </div>
                  <Button type="submit" loading={saving} className="w-full mt-3">Emitir factura</Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </form>
      </Dialog>

      {detail && <FacturaDetail fac={detail} onClose={() => setDetail(null)} />}

      {/* Dialog editar factura */}
      <Dialog open={!!editDialog} onClose={() => setEditDialog(null)} title="Editar factura" size="md">
        {editDialog && (
          <form onSubmit={submitEdit} className="space-y-4">
            <p className="text-sm text-muted-foreground">
              <span className="font-mono font-semibold">{editDialog.numero}</span> — {editDialog.cliente_nombre}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Forma de pago" className="col-span-2">
                <Select value={editForm.forma_pago} onChange={(e) => se("forma_pago", e.target.value)}>
                  {["EFECTIVO", "TRANSFERENCIA", "TARJETA", "CHEQUE", "SEGURO"].map((p) => <option key={p}>{p}</option>)}
                </Select>
              </FormField>
              {editForm.forma_pago === "CHEQUE" && (editDialog.estado === "COBRADO" || editDialog.estado === "CHEQUE_COBRADO") && (
                <div className="col-span-2 rounded-lg bg-amber-50 border border-amber-200 dark:bg-amber-900/10 dark:border-amber-700 p-3 text-xs text-amber-700 dark:text-amber-300">
                  Al cambiar a CHEQUE se resetea el estado a <strong>PENDIENTE</strong> para que puedas registrar el cheque.
                </div>
              )}
              <FormField label="Timbrado">
                <Input value={editForm.timbrado} onChange={(e) => se("timbrado", e.target.value)} />
              </FormField>
              <FormField label="Cobro estimado">
                <Input type="date" value={editForm.fecha_estimada_pago} onChange={(e) => se("fecha_estimada_pago", e.target.value)} />
              </FormField>
              <FormField label="Observaciones" className="col-span-2">
                <Input value={editForm.observaciones} onChange={(e) => se("observaciones", e.target.value)} />
              </FormField>
              {editDialog.origen === "AUTO" && (
                <FormField label="Total (Gs.)" className="col-span-2">
                  <Input type="number" min="0" value={editForm.total_general} onChange={(e) => se("total_general", e.target.value)} placeholder="Dejar vacío para no modificar" />
                </FormField>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => setEditDialog(null)}>Cancelar</Button>
              <Button type="submit" loading={savingEdit}>Guardar cambios</Button>
            </div>
          </form>
        )}
      </Dialog>

      {/* Dialog registrar cobro (completo o parcial) */}
      <Dialog open={!!pagoDialog} onClose={() => setPagoDialog(null)} title="Registrar cobro" size="md">
        {pagoDialog && (
          <form onSubmit={submitPago} className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Factura <span className="font-mono font-semibold">{pagoDialog.numero}</span> — {pagoDialog.cliente_nombre} — Total: <span className="font-mono font-semibold">{fmt(pagoDialog.total_general)}</span>
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => sp("tipo", "COMPLETO")}
                className={`flex-1 px-3 py-2 rounded-lg border-2 text-sm font-medium transition-colors ${
                  pagoForm.tipo === "COMPLETO" ? "border-green-400 bg-green-50 dark:bg-green-900/20" : "border-border hover:border-green-300"
                }`}
              >Pago completo</button>
              <button
                type="button"
                onClick={() => sp("tipo", "PARCIAL")}
                className={`flex-1 px-3 py-2 rounded-lg border-2 text-sm font-medium transition-colors ${
                  pagoForm.tipo === "PARCIAL" ? "border-orange-400 bg-orange-50 dark:bg-orange-900/20" : "border-border hover:border-orange-300"
                }`}
              >Pago parcial</button>
            </div>

            <FormField label="Forma de pago">
              <Select value={pagoForm.forma_pago} onChange={(e) => sp("forma_pago", e.target.value)}>
                {["EFECTIVO", "TRANSFERENCIA", "TARJETA", "CHEQUE", "SEGURO"].map((p) => <option key={p}>{p}</option>)}
              </Select>
            </FormField>

            {pagoForm.forma_pago === "CHEQUE" && (
              <div className="grid grid-cols-2 gap-3 rounded-lg border border-border p-3">
                <FormField label="Banco">
                  <Input value={pagoForm.cheque.banco} onChange={(e) => spc("banco", e.target.value)} placeholder="Ej: Interisa" />
                </FormField>
                <FormField label="Agencia">
                  <Input value={pagoForm.cheque.agencia} onChange={(e) => spc("agencia", e.target.value)} placeholder="Ej: CDE" />
                </FormField>
                <FormField label="Cuenta">
                  <Input value={pagoForm.cheque.cuenta} onChange={(e) => spc("cuenta", e.target.value)} />
                </FormField>
                <FormField label="N° Cheque">
                  <Input value={pagoForm.cheque.numero_cheque} onChange={(e) => spc("numero_cheque", e.target.value)} required />
                </FormField>
                <FormField label="Emitente" className="col-span-2">
                  <Input value={pagoForm.cheque.emitente} onChange={(e) => spc("emitente", e.target.value)} />
                </FormField>
                <FormField label="Fecha emisión">
                  <Input type="date" value={pagoForm.cheque.fecha_emision} onChange={(e) => spc("fecha_emision", e.target.value)} />
                </FormField>
                <FormField label="Fecha vencimiento (cheque)">
                  <Input type="date" value={pagoForm.cheque.fecha_vencimiento} onChange={(e) => spc("fecha_vencimiento", e.target.value)} />
                </FormField>
              </div>
            )}

            {pagoForm.tipo === "PARCIAL" && (
              <>
                <FormField label={`Monto abonado ahora (Gs.)${pagoForm.forma_pago === "CHEQUE" ? " — valor del cheque" : ""}`}>
                  <Input
                    type="number" min="1" step="100"
                    value={pagoForm.monto}
                    onChange={(e) => sp("monto", e.target.value)}
                  />
                </FormField>
                <div className="text-sm rounded-lg bg-orange-50 border border-orange-200 dark:bg-orange-900/10 dark:border-orange-700 p-3 text-orange-700 dark:text-orange-300">
                  Saldo pendiente: <span className="font-mono font-semibold">
                    {fmt(Math.max(pagoDialog.total_general - (parseFloat(pagoForm.monto) || 0), 0))}
                  </span> — se generará un pagaré para el cliente.
                </div>
                <FormField label="Fecha límite para pagar el saldo">
                  <Input type="date" value={pagoForm.fecha_vencimiento_saldo} onChange={(e) => sp("fecha_vencimiento_saldo", e.target.value)} />
                </FormField>
              </>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => setPagoDialog(null)}>Cancelar</Button>
              <Button type="submit" loading={savingPago}>
                {pagoForm.tipo === "PARCIAL" ? "Registrar abono y generar pagaré" : "Registrar cobro"}
              </Button>
            </div>
          </form>
        )}
      </Dialog>

      {/* Dialog liquidar saldo */}
      <Dialog open={!!liquidarDialog} onClose={() => setLiquidarDialog(null)} title="Liquidar saldo pendiente" size="md">
        {liquidarDialog && (
          <form onSubmit={submitLiquidar} className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Factura <span className="font-mono font-semibold">{liquidarDialog.numero}</span> — {liquidarDialog.cliente_nombre}
            </p>
            <div className="text-sm rounded-lg bg-green-50 border border-green-200 dark:bg-green-900/10 dark:border-green-700 p-3 text-green-700 dark:text-green-300">
              Saldo a liquidar: <span className="font-mono font-semibold">{fmt(liquidarDialog.saldo_pendiente)}</span>
            </div>
            <FormField label="Forma de pago del saldo">
              <Select value={liquidarForm.forma_pago} onChange={(e) => sl("forma_pago", e.target.value)}>
                {["EFECTIVO", "TRANSFERENCIA", "TARJETA", "CHEQUE", "SEGURO"].map((p) => <option key={p}>{p}</option>)}
              </Select>
            </FormField>
            {liquidarForm.forma_pago === "CHEQUE" && (
              <div className="grid grid-cols-2 gap-3 rounded-lg border border-border p-3">
                <FormField label="Banco">
                  <Input value={liquidarForm.cheque.banco} onChange={(e) => slc("banco", e.target.value)} placeholder="Ej: Interisa" />
                </FormField>
                <FormField label="Agencia">
                  <Input value={liquidarForm.cheque.agencia} onChange={(e) => slc("agencia", e.target.value)} placeholder="Ej: CDE" />
                </FormField>
                <FormField label="Cuenta">
                  <Input value={liquidarForm.cheque.cuenta} onChange={(e) => slc("cuenta", e.target.value)} />
                </FormField>
                <FormField label="N° Cheque">
                  <Input value={liquidarForm.cheque.numero_cheque} onChange={(e) => slc("numero_cheque", e.target.value)} required />
                </FormField>
                <FormField label="Emitente" className="col-span-2">
                  <Input value={liquidarForm.cheque.emitente} onChange={(e) => slc("emitente", e.target.value)} />
                </FormField>
                <FormField label="Fecha emisión">
                  <Input type="date" value={liquidarForm.cheque.fecha_emision} onChange={(e) => slc("fecha_emision", e.target.value)} />
                </FormField>
                <FormField label="Fecha vencimiento (cheque)">
                  <Input type="date" value={liquidarForm.cheque.fecha_vencimiento} onChange={(e) => slc("fecha_vencimiento", e.target.value)} />
                </FormField>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => setLiquidarDialog(null)}>Cancelar</Button>
              <Button type="submit" loading={savingLiquidar}>
                {liquidarForm.forma_pago === "CHEQUE" ? "Registrar cheque del saldo" : "Liquidar y generar recibo"}
              </Button>
            </div>
          </form>
        )}
      </Dialog>

      {pagareView && <Pagare data={pagareView} onClose={() => setPagareView(null)} />}
      {reciboView && <Recibo data={reciboView} onClose={() => setReciboView(null)} />}
    </div>
  );
}

function FacturaDetail({ fac, onClose }) {
  const [full, setFull] = useState(fac);
  useEffect(() => {
    api.get(`/facturas/${fac.id}`).then((r) => setFull(r.data));
  }, [fac.id]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 h-full w-full sm:w-[480px] bg-card border-l border-border flex flex-col shadow-xl overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-border sticky top-0 bg-card">
          <h2 className="font-semibold font-display">{full.numero}</h2>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-secondary text-muted-foreground">✕</button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div><span className="text-muted-foreground">Cliente: </span>{full.cliente_nombre}</div>
            <div><span className="text-muted-foreground">Ingreso vehículo: </span>{fmtDate(full.vehiculo_fecha_ingreso ?? full.fecha)}</div>
            <div><span className="text-muted-foreground">Estado: </span><Badge className={ESTADO_FAC[full.estado]}>{ESTADO_FAC_LABEL[full.estado] ?? full.estado}</Badge></div>
            <div><span className="text-muted-foreground">Forma pago: </span>{full.forma_pago}</div>
            {full.fecha_estimada_pago && (() => {
              const urg = cobroUrgencia(full.fecha_estimada_pago, full.estado);
              return (
                <div className="col-span-2">
                  <span className="text-muted-foreground">Cobro estimado: </span>
                  <span className={urg ? urg.cls : "font-medium"}>
                    {urg?.icon === "overdue" && <AlertCircle className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />}
                    {urg?.icon === "warning"  && <Clock      className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />}
                    {fmtDate(full.fecha_estimada_pago)}
                    {urg && <span className="ml-1.5 text-xs">({urg.label})</span>}
                  </span>
                </div>
              );
            })()}
            {full.estado === "PARCIAL" && (
              <>
                <div><span className="text-muted-foreground">Abonado: </span><span className="font-mono">{fmt(full.monto_pagado)}</span></div>
                <div><span className="text-muted-foreground">Saldo: </span><span className="font-mono text-orange-600 font-semibold">{fmt(full.saldo_pendiente)}</span></div>
                {full.fecha_vencimiento_saldo && (() => {
                  const urg = cobroUrgencia(full.fecha_vencimiento_saldo, full.estado);
                  return (
                    <div className="col-span-2">
                      <span className="text-muted-foreground">Vencimiento del saldo: </span>
                      <span className={urg ? urg.cls : "font-medium"}>
                        {fmtDate(full.fecha_vencimiento_saldo)}
                        {urg && <span className="ml-1.5 text-xs">({urg.label})</span>}
                      </span>
                    </div>
                  );
                })()}
              </>
            )}
            {full.estado === "COBRADO" && full.fecha_pago && (
              <div className="col-span-2"><span className="text-muted-foreground">Fecha de pago: </span>{fmtDate(full.fecha_pago)}</div>
            )}
            {full.aseguradora_nombre && (
              <div className="col-span-2"><span className="text-muted-foreground">Aseguradora: </span>{full.aseguradora_nombre}</div>
            )}
          </div>

          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[360px]">
            <thead>
              <tr className="border-b border-border text-xs text-muted-foreground">
                <th className="text-left pb-1">Descripción</th>
                <th className="text-right pb-1">Cant</th>
                <th className="text-right pb-1">P.Unit</th>
                <th className="text-right pb-1">IVA</th>
                <th className="text-right pb-1">Total</th>
              </tr>
            </thead>
            <tbody>
              {(full.items ?? []).map((i) => (
                <tr key={i.id} className="border-b border-border/50">
                  <td className="py-1">{i.descripcion}</td>
                  <td className="text-right py-1 font-mono">{i.cantidad}</td>
                  <td className="text-right py-1 font-mono">{fmt(i.precio_unitario)}</td>
                  <td className="text-right py-1 text-xs text-muted-foreground">{i.tipo_iva}</td>
                  <td className="text-right py-1 font-mono">{fmt(i.subtotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>

          <div className="text-sm space-y-1 text-right">
            <div className="flex justify-end gap-8"><span className="text-muted-foreground">Exentas</span><span className="font-mono">{fmt(full.total_exentas)}</span></div>
            <div className="flex justify-end gap-8"><span className="text-muted-foreground">Gravadas 5%</span><span className="font-mono">{fmt(full.total_iva5)}</span></div>
            <div className="flex justify-end gap-8"><span className="text-muted-foreground">Gravadas 10%</span><span className="font-mono">{fmt(full.total_iva10)}</span></div>
            <div className="flex justify-end gap-8"><span className="text-muted-foreground">IVA</span><span className="font-mono">{fmt(full.total_iva)}</span></div>
            <div className="flex justify-end gap-8 font-bold text-lg"><span>TOTAL</span><span className="font-mono text-primary">{fmt(full.total_general)}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}
